import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { useState } from 'react';

export type InlineAlertVariant = 'warning' | 'info' | 'error' | 'success';
export type InlineAlertSize = 'sm' | 'md' | 'lg' | 'line';

interface InlineAlertAction {
  label: string;
  onClick: () => void;
}

export interface InlineAlertProps {
  variant?: InlineAlertVariant;
  size?: InlineAlertSize;
  title?: string;
  children?: React.ReactNode;
  dismissible?: boolean;
  action?: InlineAlertAction;
  className?: string;
}

// Un ícono distinto por variante: antes warning y error compartían AlertCircle,
// lo que los hacía indistinguibles para alguien que no percibe bien el color.
const VARIANT_ICON: Record<InlineAlertVariant, typeof AlertTriangle> = {
  warning: AlertTriangle,
  error: XCircle,
  info: Info,
  success: CheckCircle2,
};

const VARIANT_STYLES: Record<InlineAlertVariant, { wrapper: string; iconColor: string; actionColor: string }> = {
  warning: {
    wrapper: 'border-[color:var(--warning-border)] bg-warning-bg',
    iconColor: 'text-warning',
    actionColor: 'var(--warning)',
  },
  info: {
    wrapper: 'border-[color:var(--info-border)] bg-info-bg',
    iconColor: 'text-info',
    actionColor: 'var(--info)',
  },
  error: {
    wrapper: 'border-[color:var(--error-border)] bg-error-bg',
    iconColor: 'text-error',
    actionColor: 'var(--error)',
  },
  success: {
    wrapper: 'border-[color:var(--success-border)] bg-success-bg',
    iconColor: 'text-success',
    actionColor: 'var(--success)',
  },
};

// Todas las medidas de bloque (padding, gap, radio, tamaños de ícono) usan
// números pares. Los textos (12/13/15px) quedan como estaban en el
// componente original — ahí no hay restricción.
const SIZE_STYLES: Record<
  InlineAlertSize,
  {
    wrapper: string;
    iconSize: number;
    closeIconSize: number;
    titleClass: string;
    descClass: string;
    // Espacio que separa la descripción del título — solo debe aplicarse
    // cuando hay título encima; si no, deja al texto "caído" sin motivo.
    descSpacingWithTitle: string;
    layout: 'stacked' | 'line';
  }
> = {
  sm: {
    wrapper: 'gap-2 rounded-md px-2.5 py-2',
    iconSize: 14,
    closeIconSize: 14,
    titleClass: 'text-[12px] leading-[18px] font-medium',
    descClass: 'text-[12px] leading-[18px] font-normal',
    descSpacingWithTitle: 'mt-0.5',
    layout: 'stacked',
  },
  md: {
    wrapper: 'gap-2 rounded-lg px-3 py-2.5',
    iconSize: 16,
    closeIconSize: 14,
    titleClass: 'text-[13px] leading-[20px] font-semibold',
    descClass: 'text-[13px] leading-[20px] font-normal',
    descSpacingWithTitle: 'mt-1',
    layout: 'stacked',
  },
  lg: {
    wrapper: 'gap-2.5 rounded-[10px] px-4 py-3.5',
    iconSize: 20,
    closeIconSize: 16,
    titleClass: 'text-[15px] leading-[20px] font-semibold',
    descClass: 'text-[14px] leading-[22px] font-normal mb-2',
    descSpacingWithTitle: 'mt-1',
    layout: 'stacked',
  },
  line: {
    wrapper: 'gap-2 rounded-lg px-3.5 py-2',
    iconSize: 16,
    closeIconSize: 14,
    titleClass: 'text-[13px] leading-[20px] font-medium',
    descClass: 'text-[13px] leading-[20px] font-normal',
    descSpacingWithTitle: 'ml-1',
    layout: 'line',
  },
};

export const InlineAlert = ({
  variant = 'warning',
  size = 'md',
  title,
  children,
  dismissible = false,
  action,
  className = '',
}: InlineAlertProps) => {
  const [visible, setVisible] = useState(true);
  const [closing, setClosing] = useState(false);

  if (!visible) return null;

  const variantStyles = VARIANT_STYLES[variant];
  const sizeStyles = SIZE_STYLES[size];
  const Icon = VARIANT_ICON[variant];

  const handleDismiss = () => {
    setClosing(true);
    // 220ms coincide con la duración de 'fadeOut' definida en tailwind.config
    window.setTimeout(() => setVisible(false), 220);
  };

  const role = variant === 'error' ? 'alert' : 'status';
  const ariaLive = variant === 'error' ? 'assertive' : 'polite';

  if (sizeStyles.layout === 'line') {
    return (
      <div
        role={role}
        aria-live={ariaLive}
        className={`flex items-center border ${variantStyles.wrapper} ${sizeStyles.wrapper} ${
          closing ? 'animate-fadeOut' : 'animate-fadeIn'
        } ${className}`}
      >
        <Icon size={sizeStyles.iconSize} className={`shrink-0 ${variantStyles.iconColor}`} />
        <div className="flex items-baseline min-w-0">
          {title && <span className={`text-secondary ${sizeStyles.titleClass}`}>{title}</span>}
          {children && (
            <span className={`text-secondary ${sizeStyles.descClass} ${title ? sizeStyles.descSpacingWithTitle : ''}`}>
              {children}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      role={role}
      aria-live={ariaLive}
      className={`flex items-start border ${variantStyles.wrapper} ${sizeStyles.wrapper} ${
        closing ? 'animate-fadeOut' : 'animate-fadeIn'
      } ${className}`}
    >
      <Icon size={sizeStyles.iconSize} className={`shrink-0 mt-0.5 ${variantStyles.iconColor}`} />
      <div className="flex-1 min-w-0">
        {title && <p className={`text-secondary ${sizeStyles.titleClass}`}>{title}</p>}
        {children && (
          <div className={`text-secondary ${sizeStyles.descClass} ${title ? sizeStyles.descSpacingWithTitle : ''}`}>
            {children}
          </div>
        )}
        {action && (
          <button
            onClick={action.onClick}
            style={{ color: variantStyles.actionColor }}
            className="text-[13px] font-medium bg-transparent border-none p-0 cursor-pointer hover:underline"
          >
            {action.label}
          </button>
        )}
      </div>
      {dismissible && (
        <button
          onClick={handleDismiss}
          aria-label="Cerrar"
          className={`shrink-0 p-0.5 rounded bg-transparent border-none cursor-pointer opacity-60 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--border-focus)] ${variantStyles.iconColor}`}
        >
          <X size={sizeStyles.closeIconSize} />
        </button>
      )}
    </div>
  );
};
